import { db } from '../../firebase/config'

// De te vertalen teksten van één vragenlijst, in één set.
//
// Twee schermen tonen dezelfde vragenlijst aan een respondent: het invulscherm
// en het printvel. Beide moeten precies dezelfde teksten laten vertalen — mist
// er één, dan komt er geen fout maar staat er Nederlands tussen het Turks.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// TWEE SOORTEN SLEUTELS, en die asymmetrie moet blijven: een vraag wordt op zijn
// veld-ID gesleuteld (`translations[field.ID]`), een antwoordtekst op zichzelf
// (`translations[option.Option]`). Elke afnemer leest ze zo, dus wie hier één
// kant omgooit breekt de andere zonder foutmelding.

/** Firestore weigert een `in`-query met meer dan tien waarden. */
const CHUNK_SIZE = 10

const chunk = (arr, size) => {
    const out = []
    for (let i = 0; i < (arr?.length || 0); i += size) out.push(arr.slice(i, i + size))
    return out
}

const dedupe = (arr) => Array.from(new Set((arr || []).filter(Boolean)))

/**
 * De standaard-ophaler: tenant-gescoped, net als de hook-engine.
 *
 * Een ongescopede read matcht stil documenten van andere omgevingen — en het
 * invulscherm draait op een publieke route.
 * AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
 *
 * @param {string} collection
 * @param {Array<string>} fieldIds - Ten hoogste tien
 * @param {string} client - CompagnyID
 * @returns {Promise<Array<object>>}
 */
const fetchByField = async (collection, fieldIds, client) => {
    const snap = await db.collection(collection)
        .where('CompagnyID', '==', client)
        .where('Field', 'in', fieldIds)
        .get()

    return snap.docs.map((doc) => doc.data())
}

/**
 * @param {object} params
 * @param {Array<object>} params.questionnaires - De `Questionnaires`-docs
 * @param {Array<object>} params.fields - De `QuestionnaireFields` in weergavevolgorde
 * @param {string} params.client - CompagnyID, voor de tenant-scope van de reads
 * @param {Object<string, any>} [params.base] - Eigen woorden van het scherm
 *   (knoppen, aanhef). Elk scherm heeft andere, dus die horen niet hier
 * @param {(collection: string, fieldIds: Array<string>, client: string) => Promise<Array<object>>} [params.fetchDocs]
 * @returns {Promise<Object<string, any>>} De set voor `setTranslationInput`
 */
const buildTranslationStrings = async ({
    questionnaires,
    fields,
    client,
    base = {},
    fetchDocs = fetchByField,
}) => {
    const strings = { ...base }

    ;(questionnaires || []).forEach((questionnaire) => {
        strings[questionnaire.ID] = { title: questionnaire.Title, description: questionnaire.Description }
    })

    ;(fields || []).forEach((field) => {
        if (field?.Question) strings[field.ID] = field.Question
        if (field?.Explainer) strings[`${field.ID}_explainer`] = field.Explainer
        if (field?.Type === 'scale') {
            if (field?.ReachStartLable) strings[field.ReachStartLable] = field.ReachStartLable
            // Zelfde begintekst in de goede spelling (44 vragen); de schuifbalk leest beide.
            if (field?.ReachStartLabel) strings[field.ReachStartLabel] = field.ReachStartLabel
            if (field?.ReachEndLabel) strings[field.ReachEndLabel] = field.ReachEndLabel
        }
    })

    const idsOfType = (types) => (fields || [])
        .filter((field) => types.includes(field?.Type))
        .map((field) => field.ID)

    const multipleChunks = chunk(idsOfType(['multiple-one', 'multiple-multiple']), CHUNK_SIZE)
    const matrixChunks = chunk(idsOfType(['matrix-one', 'matrix-multiple']), CHUNK_SIZE)

    const gather = (collection, chunks) => chunks.map((ids) => fetchDocs(collection, ids, client))

    const [options, rows, columns] = await Promise.all([
        Promise.all(gather('MultipleQuestionOptions', multipleChunks)),
        Promise.all(gather('MatrixQuestionRows', matrixChunks)),
        Promise.all(gather('MatrixQuestionColumns', matrixChunks)),
    ])

    dedupe(options.flat().map((option) => option?.Option)).forEach((text) => { strings[text] = text })
    dedupe(rows.flat().map((row) => row?.Title)).forEach((text) => { strings[text] = text })
    dedupe(columns.flat().map((column) => column?.Title)).forEach((text) => { strings[text] = text })

    return strings
}

export default buildTranslationStrings
